import { useMemo, useState } from "react"

import { TAB_KEYS_BY_PAGE } from "@/const/tab.constants"
import { isDataAvailable } from "@/helpers/context.helper"
import { scrollToElement } from "@/helpers/table.helpers"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useOrderBy } from "@/hooks/useOrderBy"
import { useQueryString } from "@/hooks/useQueryString"
import { useTooltip } from "@/hooks/useTooltip"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { ExpandIconTable } from "@/molecules/ExpandIconTable"
import { TotalPagination } from "@/molecules/TotalPagination"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import {
  UsePackageReferencesCount,
  useSbomInventory
} from "@/pages/DiscoveryPage/application/sbom-inventory/useSbomInventory"
import { getFilters } from "@/pages/DiscoveryPage/application/sbom-inventory/useSbomInventory.selector"
import { TabTemplate } from "@/templates/TabTemplate"
import { ExpandedRowSbomTab } from "../ExpandedRowSbomTab"
import { SbomFilter } from "../SbomFilter"
import { getColumnsTable } from "./getColumnsTable"
import { HeaderSbomTab } from "./HeaderSbomTab"

import type { SbomTabComponent } from "."

export const SbomTab: SbomTabComponent = ({ useActiveKeyTabsInventory }) => {
  const { onSorterChange, ...orderByProps } = useOrderBy()
  const { t } = useTranslation()
  const convertors = useNumberConvertor()
  const { onRowClick } = useDrawer("")
  const { activeKey, config = {} } = useActiveKeyTabsInventory()
  const previewMode = !!config.headers
  const columns = getColumnsTable({
    t,
    convertors,
    orderByProps,
    previewMode,
    onRowClick
  })
  const TAB_KEYS = TAB_KEYS_BY_PAGE.INVENTORY

  const filterKeys = useMemo(() => getFilters({ t }).map(({ key }) => key), [t])

  const params = useQueryString(["orderBy", "ascending", ...filterKeys])
  const [context] = useSbomInventory(params, {
    enabled: activeKey === TAB_KEYS[4],
    disableCountRequest: true,
    ...config
  })
  const { response, isLoading } = context
  const { data, metadata } = response || {}
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const countContext = UsePackageReferencesCount(params, config)
  const {
    isLoading: isCountLoading,
    response: countResponse = { data: undefined }
  } = countContext

  useTooltip(data)

  const dataReadinessFF = useFeatureFlags("dataReadiness")
  const filterDisabled = !!dataReadinessFF

  return (
    <TabTemplate
      sticky
      columns={columns}
      dataSource={data}
      metadata={metadata}
      loading={isLoading}
      headerComponent={<HeaderSbomTab />}
      expandable={
        !previewMode
          ? {
              expandedRowKeys,
              expandIcon: ExpandIconTable,
              expandRowByClick: true,
              rowExpandable: ({ expandable }) => !!expandable,
              expandedRowRender: ({ key }, _index, _, expanded) => (
                <ExpandedRowSbomTab
                  packageId={key}
                  expanded={expanded}
                  params={params}
                  onCollapsed={() => {
                    scrollToElement(key, _index)
                    setExpandedRowKeys(
                      expandedRowKeys.filter((value) => value !== key)
                    )
                  }}
                />
              ),
              onExpand: (expanded, record) => {
                if (expanded)
                  setExpandedRowKeys([record.key, ...expandedRowKeys])
                else
                  setExpandedRowKeys(
                    expandedRowKeys.filter((value) => value !== record.key)
                  )
              }
            }
          : undefined
      }
      totalRender={({ loading, total }) => (
        <TotalPagination
          totals={[
            {
              translationKey: "general.package",
              value: total
            },
            {
              translationKey: "general.result",
              value: countResponse.data
            }
          ]}
          isLoading={loading || isCountLoading}
        />
      )}
      SearchComponent={
        !previewMode && (
          <SbomFilter
            isLoading={filterDisabled}
            previewMode={previewMode}
            exportType="Sbom"
            isDataAvailable={isDataAvailable(context)}
            packagesReferencesCount={countResponse.data}
            searchedVulnerabilities={metadata?.searchedVulnerabilities}
          />
        )
      }
      onChange={(_pagination, _filters, sorter) => {
        onSorterChange(sorter)
      }}
    />
  )
}
